import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikProvider, useFormik } from "formik";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcSettingSwitchFormik,
} from "oc/components";
import { useOcProperty } from "oc/hooks";
import { useOcFormikHelper } from "oc/utils";
import { Box } from "@mui/material";

interface MyHrSettingsTaxCreditApplicationCardProps {
  saveTaxApplicationsEnabled: boolean;
  isMobile: boolean;
}

export const MyHrSettingsTaxCreditApplicationCard = ({
  saveTaxApplicationsEnabled,
  isMobile,
}: MyHrSettingsTaxCreditApplicationCardProps) => {
  const { t } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: taxApplicationSaveEnabledValue, loading: taxApplicationSaveEnabledLoading } = useOcProperty({
    resource: "system",
    key: "taxApplicationSaveEnabled",
    fetchPolicy: "network-only",
  });

  const { data: taxApplicationsMenuEnabledValue, loading: taxApplicationsMenuEnabledLoading } = useOcProperty({
    resource: "system",
    key: "taxApplicationsMenuEnabled",
    fetchPolicy: "network-only",
  });

  const formik = useFormik({
    initialValues: {
      taxApplicationSaveEnabled: taxApplicationSaveEnabledValue === "true",
      taxApplicationsMenuEnabled: taxApplicationsMenuEnabledValue === "true",
    },
    onSubmit: () => {
      updateProperties();
    },
    enableReinitialize: true,
  });

  const { loadingState, updateProperties } = useOcFormikHelper({
    defaultResource: "system",
    formik,
    valuesExtraData: {
      taxApplicationSaveEnabled: {
        key: "taxApplicationSaveEnabled",
        loading: taxApplicationSaveEnabledLoading,
        type: "switch",
        returnType: "boolean",
      },
      taxApplicationsMenuEnabled: {
        key: "taxApplicationsMenuEnabled",
        loading: taxApplicationsMenuEnabledLoading,
        type: "switch",
        returnType: "boolean",
      },
    },
  });

  const handleSave = () => {
    setShowConfirmDialog(false);
    formik.submitForm();
  };

  const handleCancel = () => {
    formik.resetForm();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_TAX_CREDIT_APPLICATIONS")} loading={loadingState}>
        <FormikProvider value={formik}>
          <Box height={8} />
          <OcSettingSwitchFormik
            name="taxApplicationsMenuEnabled"
            id="enabledTaxApplicationsMenu"
            label={t("MY_TAX_CREDIT_APPLICATIONS")}
            info={t("MY_TAX_APPLICATIONS_MENU_INFO")}
            isMobile={isMobile}
            disabled={!saveTaxApplicationsEnabled || loadingState}
          />
          <Box height={32} />
          <OcSettingSwitchFormik
            name="taxApplicationSaveEnabled"
            id="enabledTaxSave"
            label={t("OC_SAVE")}
            info={t("MY_TAX_TAX_CREDIT_APPLICATIONS_SETTING_INFO")}
            isMobile={isMobile}
            disabled={!saveTaxApplicationsEnabled || loadingState || taxApplicationsMenuEnabledValue !== "true"}
          />
          <OcCardButtonBar
            handleRightButton={handleCancel}
            rightButtonColor="secondary"
            rightButtonText={t("OC_CANCEL")}
            handlePrimaryButton={() => setShowConfirmDialog(true)}
            primaryButtonText={t("OC_SAVE")}
            primaryButtonDisabled={loadingState || !formik.dirty}
          />
        </FormikProvider>
      </OcCard>
    </>
  );
};

export default MyHrSettingsTaxCreditApplicationCard;
